/**
 * Simple in-memory cache with TTL (Time To Live) support
 * Helps reduce database reads by caching frequently accessed data
 */

// Entries are mirrored into sessionStorage so a page reload (or opening the
// same screen in another tab) reuses the data instead of re-hitting the API.
// That directly cuts backend Firestore reads.
const STORAGE_PREFIX = 'odenta_cache_';

const storage = (() => {
  try {
    const probe = '__odenta_probe__';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch (_) {
    return null; // private mode / storage disabled
  }
})();

class Cache {
  constructor() {
    this.cache = new Map();
    this.timers = new Map();
    // Promises for keys currently being fetched, so two components mounting at
    // once share a single request instead of firing duplicates.
    this.inFlight = new Map();
    this.generations = new Map();
  }

  _persist(key, entry) {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch (_) {
      // Quota exceeded - the in-memory cache still works.
    }
  }

  _readPersisted(key) {
    if (!storage) return null;
    try {
      const raw = storage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || typeof entry.timestamp !== 'number') return null;
      if (Date.now() - entry.timestamp > entry.ttl) {
        storage.removeItem(STORAGE_PREFIX + key);
        return null;
      }
      return entry;
    } catch (_) {
      return null;
    }
  }

  _dropPersisted(key) {
    if (!storage) return;
    try {
      storage.removeItem(STORAGE_PREFIX + key);
    } catch (_) {}
  }

  /**
   * Set a value in cache with optional TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
   */
  set(key, value, ttl = 5 * 60 * 1000) {
    // Clear existing timer if any
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
    }

    // Set the value
    const entry = {
      value,
      timestamp: Date.now(),
      ttl
    };
    this.cache.set(key, entry);
    this._persist(key, entry);

    // Set expiration timer
    const timer = setTimeout(() => {
      this.delete(key);
    }, ttl);

    this.timers.set(key, timer);
  }

  /**
   * Get a value from cache
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null if not found/expired
   */
  get(key) {
    let item = this.cache.get(key);

    if (!item) {
      // Fall back to the reload-surviving copy.
      item = this._readPersisted(key);
      if (!item) return null;
      this.cache.set(key, item);
    }

    // Check if expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.delete(key);
      return null;
    }

    return item.value;
  }

  /**
   * Check if a key exists in cache and is not expired
   * @param {string} key - Cache key
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Delete a key from cache
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
    this._dropPersisted(key);
    this.generations.set(key, (this.generations.get(key) || 0) + 1);
    this.inFlight.delete(key);

    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key));
      this.timers.delete(key);
    }
  }

  /**
   * Clear all cache
   */
  clear() {
    // Clear all timers
    this.timers.forEach(timer => clearTimeout(timer));

    // Clear cache and timers
    this.cache.clear();
    this.timers.clear();
    this.inFlight.clear();
    this.generations.clear();

    if (storage) {
      try {
        Object.keys(storage)
          .filter(k => k.startsWith(STORAGE_PREFIX))
          .forEach(k => storage.removeItem(k));
      } catch (_) {}
    }
  }

  /**
   * Get cache statistics
   * @returns {object} Cache stats
   */
  getStats() {
    const keys = new Set(this.cache.keys());
    if (storage) {
      try {
        Object.keys(storage)
          .filter(k => k.startsWith(STORAGE_PREFIX))
          .forEach(k => keys.add(k.slice(STORAGE_PREFIX.length)));
      } catch (_) {}
    }
    return {
      size: keys.size,
      keys: Array.from(keys)
    };
  }

  /** Drop every key matching a prefix (in memory and on disk). */
  invalidatePrefix(prefix) {
    this.getStats().keys
      .filter(key => key.startsWith(prefix))
      .forEach(key => this.delete(key));
  }

  /**
   * Get or set pattern - if key exists return it, otherwise execute function and cache result
   * @param {string} key - Cache key
   * @param {function} fn - Function to execute if cache miss
   * @param {number} ttl - Time to live in milliseconds
   * @param {boolean} silent - If true, do not log cache hit/miss (use in production)
   * @returns {Promise<any>} Cached or fresh value
   */
  async getOrSet(key, fn, ttl = 5 * 60 * 1000, silent = true) {
    const cached = this.get(key);
    if (cached !== null) {
      if (!silent) console.log(`Cache HIT: ${key}`);
      return cached;
    }

    // Share an in-flight request rather than issuing a duplicate one.
    if (this.inFlight.has(key)) {
      return this.inFlight.get(key);
    }

    if (!silent) console.log(`Cache MISS: ${key}`);

    const startedAtGeneration = this.generations.get(key) || 0;
    const promise = (async () => {
      try {
        const value = await fn();
        if ((this.generations.get(key) || 0) === startedAtGeneration) {
          this.set(key, value, ttl);
        }
        return value;
      } finally {
        this.inFlight.delete(key);
      }
    })();

    this.inFlight.set(key, promise);
    return promise;
  }
}

// Create a singleton instance
const cache = new Cache();

// Cache key generators for common data types (app-wide, all roles)
export const CacheKeys = {
  // Assistant / Admin (existing)
  PATIENTS: (page = 1, limit = 20, search = '', studentFilter = 'all', sortBy = 'registrationDate', sortOrder = 'desc') =>
    `patients_${page}_${limit}_${search}_${studentFilter}_${sortBy}_${sortOrder}`,
  // Full unpaginated patient list. Deliberately NOT the same key as PATIENTS():
  // that endpoint returns a bare array here but {patients, pagination} when
  // paginated, and sharing one key made the two shapes overwrite each other.
  PATIENTS_ALL: () => 'patients_all',
  ASSISTANT_ANALYTICS: (scope = 'all', year = 0, month = 0) => `analytics_data_${scope}_${year}_${month}`,
  ASSISTANT_APPOINTMENT_ANALYTICS: (scope = 'month', year = 0, month = 0) =>
    `analytics_appointments_${scope}_${year}_${month}`,
  PATIENT_STATS: () => 'analytics_patient_stats',
  PATIENT_ENTRIES: (year, month) => `patient_entries_${year}_${month}`,
  STUDENTS: (page = 1, limit = 50, search = '') => `students_${page}_${limit}_${search}`,
  PROCEDURE_REQUESTS: () => 'procedure_requests',
  PATIENT_DETAILS: (nationalId) => `patient_details_${nationalId}`,
  PATIENT_APPOINTMENTS: (nationalId) => `patient_appointments_${nationalId}`,
  USER_PROFILE: (userId) => `user_profile_${userId}`,

  // Student
  STUDENT_PATIENTS: (userId) => `student_patients_${userId}`,
  STUDENT_APPOINTMENTS: (userId) => `student_appointments_${userId}`,
  STUDENT_PROCEDURE_REQUESTS: (userId) => `student_procedure_requests_${userId}`,
  STUDENT_LAB_REQUESTS: (userId) => `student_lab_requests_${userId}`,
  STUDENT_ANALYTICS: (userId) => `student_analytics_${userId}`,
  STUDENT_REVIEWS: (studentId) => `student_reviews_${studentId}`,

  // Patient public (shared across student, dentist, assistant - single patient view)
  PATIENT_PUBLIC: (nationalId) => `patient_public_${nationalId}`,
  PATIENT_PUBLIC_FULL: (nationalId) => `patient_public_full_${nationalId}`,

  // Supervisor
  SUPERVISOR_REVIEWS: (userId) => `supervisor_reviews_${userId}`,
  SUPERVISOR_SIGNATURE: (userId) => `supervisor_signature_${userId}`,
  SUPERVISOR_STUDENTS: (university) => `supervisor_students_${university}`,
  SUPERVISOR_LAB_REQUESTS: (userId) => `supervisor_lab_requests_${userId}`,
  SUPERVISOR_ANALYTICS: (userId, timeRange) => `supervisor_analytics_${userId}_${timeRange}`,
  SUPERVISOR_PATIENT: (nationalId) => `supervisor_patient_${nationalId}`,
  SUPERVISOR_STUDENT_REVIEWS: (studentId) => `supervisor_student_reviews_${studentId}`,

  // Admin
  ADMIN_PATIENTS: (university) => `admin_patients_${university}`,
  ADMIN_STUDENTS: (university) => `admin_students_${university}`,
  ADMIN_ASSISTANTS: (university) => `admin_assistants_${university}`,
  ADMIN_SUPERVISORS: (university) => `admin_supervisors_${university}`,
  ADMIN_APPOINTMENTS: (university) => `admin_appointments_${university}`,
  ADMIN_TREATMENT_SHEETS: () => 'admin_treatment_sheets',
  ADMIN_NEWS: (university) => `admin_news_${university}`,
  ADMIN_REVIEWS: (university) => `admin_reviews_${university}`,

  // Superadmin
  UNIVERSITIES: () => 'universities',
  CLINICS: () => 'clinics',
  SUPERADMIN_ACCOUNTS: (page, limit) => `superadmin_accounts_${page}_${limit}`,
  SUPERADMIN_ACTIVITY: (page, limit) => `superadmin_activity_${page}_${limit}`,
  SUPERADMIN_ANALYTICS: () => 'superadmin_analytics',
  SUPERADMIN_NEWS: () => 'superadmin_news',

  // Dentist
  DENTIST_PATIENTS: (userId) => `dentist_patients_${userId}`,
  DENTIST_APPOINTMENTS: (userId) => `dentist_appointments_${userId}`,
  DENTIST_TODOS: (userId) => `dentist_todos_${userId}`,

  // Lab requests (admin/assistant)
  LAB_REQUESTS: () => 'lab_requests',

  // IT
  IT_STATS: () => 'it_stats',
  IT_ACCOUNTS: (page, limit) => `it_accounts_${page}_${limit}`,
};

// Cache TTL constants (in milliseconds)
export const CacheTTL = {
  SHORT: 2 * 60 * 1000,      // 2 minutes
  MEDIUM: 5 * 60 * 1000,     // 5 minutes
  LONG: 15 * 60 * 1000,      // 15 minutes
  VERY_LONG: 60 * 60 * 1000  // 1 hour
};

export default cache;
